/**
 * useConsonantReviewFlow — State machine for the 6-prompt consonant practice session
 *
 * Session-level flow (prescribed card order):
 *   awaiting_scan → prompt1 → prompt2 (pre-video → videos → post-video) → prompt3
 *   → prompt4a → prompt4b → prompt5a → prompt5b → prompt6 → awaiting_scan (next card)
 *
 * Each step emits audio via AudioSegment[] through playAudioSequence().
 * Video steps (prompt2 mid-section, prompt4a, prompt5a) trigger video playback
 * via the `activeVideo` state — the UI component owns the <video> element.
 *
 * Button logic:
 * - Restart: replays current step
 * - Continue: advances to next step
 * - Show Me How (4b, 5b only): replays 4a/5a video without reverting step
 *
 * @see consonantReviewService.ts for audio segment builders
 * @see audio-engine.ts for AudioSegment type and playAudioSequence()
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import { useTranslation } from '@/lib/i18n';
import type { CardImage } from '@/services/qrCodeService';
import { lookupQRCode } from '@/services/qrCodeService';
import { getSkillIdForPhoneme, type PhonemeExposure } from '@/services/learningProgressService';
import { playAudioSequence, stopAllAudio } from '@/lib/voice-agent/audio-engine';
import {
  CARD_ORDER,
  getIllustrationWord,
  getPrompt1Audio,
  getPrompt2PreVideoAudio,
  getPrompt2PostVideoAudio,
  getPrompt3Audio,
  getPrompt3CaptureAudio,
  getPrompt4aAudio,
  getPrompt4bAudio,
  getPrompt5aAudio,
  getPrompt5bAudio,
  getScanPromptAudio,
  getNudgeAudio,
  clearPhraseCache,
} from '@/services/consonantReviewService';

// =============================================================================
// Types
// =============================================================================

export type SessionStep =
  | 'awaiting_scan'
  | 'prompt1'
  | 'prompt2_pre_video'
  | 'prompt2_video'
  | 'prompt2_post_video'
  | 'prompt3'
  | 'prompt3_capture'
  | 'prompt4a'
  | 'prompt4a_video'
  | 'prompt4b'
  | 'prompt5a'
  | 'prompt5a_video'
  | 'prompt5b'
  | 'prompt6'
  | 'session_complete';

export type VideoType =
  | 'lowercase_image_live'
  | 'uppercase_image_live'
  | 'lowercase_tracing'
  | 'uppercase_tracing'
  | null;

export interface ScannedCard {
  qrCode: { id: string; name: string; action_type: string | null; action_payload: unknown };
  cardImages: CardImage[];
  phoneme: string;
  displayName: string;
  skillId?: string;
  legacyImagePath?: string | null;
  illustrationWord?: string;
  illustrationDescription?: string;
  /** Single coloring video of the full card — cropped left for LC, right for UC */
  coloringVideo?: string;
  lowercaseImageLiveVideo?: string;
  uppercaseImageLiveVideo?: string;
}

export interface TrackedPhoneme extends PhonemeExposure {
  displayName: string;
}

export interface ReviewFlowState {
  step: SessionStep;
  scannedCard: ScannedCard | null;
  isPlaying: boolean;
  activeVideo: VideoType;
  scanFeedback: { success: boolean; message: string } | null;
  currentCardIndex: number;
  expectedPhoneme: string;
  completedCards: string[];
}

export interface ReviewFlowActions {
  handleQRScan: (code: string) => Promise<void>;
  handleQRError: (error: string) => void;
  handleRestart: () => void;
  handleContinue: () => void;
  handleShowMeHow: () => void;
  handleMasterySelection: (feedback: 'learning' | 'mastered') => void;
  handleVideoEnded: () => void;
  phonemesScanned: React.MutableRefObject<TrackedPhoneme[]>;
}

// =============================================================================
// Step metadata
// =============================================================================

const STEP_LABELS: Record<SessionStep, string> = {
  awaiting_scan: 'Scan Card',
  prompt1: 'Listen & Say',
  prompt2_pre_video: 'Illustration',
  prompt2_video: 'Watch',
  prompt2_post_video: 'Draw It!',
  prompt3: 'Practice',
  prompt3_capture: 'Your Turn!',
  prompt4a: 'Watch Tracing',
  prompt4a_video: 'Watch Tracing',
  prompt4b: 'Trace Lowercase',
  prompt5a: 'Watch Tracing',
  prompt5a_video: 'Watch Tracing',
  prompt5b: 'Trace Uppercase',
  prompt6: 'How Did It Go?',
  session_complete: 'Complete',
};

export { STEP_LABELS };

// =============================================================================
// Hook
// =============================================================================

export function useConsonantReviewFlow(
  childId: string | undefined,
  childName: string
): ReviewFlowState & ReviewFlowActions & { stepLabels: Record<SessionStep, string> } {
  const { t } = useTranslation();
  const [step, setStep] = useState<SessionStep>('awaiting_scan');
  const [scannedCard, setScannedCard] = useState<ScannedCard | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [activeVideo, setActiveVideo] = useState<VideoType>(null);
  const [scanFeedback, setScanFeedback] = useState<{ success: boolean; message: string } | null>(null);
  const [currentCardIndex, setCurrentCardIndex] = useState(0);
  const [completedCards, setCompletedCards] = useState<string[]>([]);

  const phonemesScanned = useRef<TrackedPhoneme[]>([]);
  const feedbackTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const abortRef = useRef(false);
  const flowGenRef = useRef(0);
  const illustrationWordRef = useRef<string | undefined>(undefined);

  const expectedPhoneme = CARD_ORDER[currentCardIndex] || '';

  const stepLabels: Record<SessionStep, string> = {
    awaiting_scan: t('Scan Card'),
    prompt1: t('Listen & Say'),
    prompt2_pre_video: t('Illustration'),
    prompt2_video: t('Watch'),
    prompt2_post_video: t('Draw It!'),
    prompt3: t('Practice'),
    prompt3_capture: t('Your Turn!'),
    prompt4a: t('Watch Tracing'),
    prompt4a_video: t('Watch Tracing'),
    prompt4b: t('Trace Lowercase'),
    prompt5a: t('Watch Tracing'),
    prompt5a_video: t('Watch Tracing'),
    prompt5b: t('Trace Uppercase'),
    prompt6: t('How Did It Go?'),
    session_complete: t('Complete'),
  };

  // ── Helpers ──────────────────────────────────────────────────────────────

  const showFeedback = useCallback((success: boolean, message: string, duration = 3000) => {
    if (feedbackTimerRef.current) clearTimeout(feedbackTimerRef.current);
    setScanFeedback({ success, message });
    feedbackTimerRef.current = setTimeout(() => setScanFeedback(null), duration);
  }, []);

  const playSegments = useCallback(async (
    segments: import('@/lib/voice-agent/audio-engine').AudioSegment[],
    gen: number
  ) => {
    const isStale = () => abortRef.current || flowGenRef.current !== gen;
    if (isStale()) return;
    setIsPlaying(true);
    try {
      await playAudioSequence(segments, isStale);
    } catch (err) {
      console.error('[ReviewFlow] Audio failed:', err);
    } finally {
      if (!isStale()) setIsPlaying(false);
    }
  }, []);

  // ── Step Execution ──────────────────────────────────────────────────────

  const executeStep = useCallback(async (
    targetStep: SessionStep,
    card: ScannedCard,
    gen: number
  ) => {
    const isStale = () => abortRef.current || flowGenRef.current !== gen;
    if (isStale()) return;

    const phoneme = card.phoneme;
    const illustration = card.illustrationWord || getIllustrationWord(phoneme);

    switch (targetStep) {
      case 'prompt1':
        await playSegments(getPrompt1Audio(phoneme), gen);
        break;

      case 'prompt2_pre_video':
        await playSegments(getPrompt2PreVideoAudio(phoneme, illustration), gen);
        if (!isStale()) {
          setStep('prompt2_video');
          setActiveVideo('lowercase_image_live');
        }
        return;

      case 'prompt2_post_video':
        await playSegments(getPrompt2PostVideoAudio(illustration), gen);
        break;

      case 'prompt3':
        await playSegments(getPrompt3Audio(phoneme, illustration), gen);
        if (!isStale()) {
          setStep('prompt3_capture');
          executeStep('prompt3_capture', card, gen);
        }
        return;

      case 'prompt3_capture':
        await playSegments(getPrompt3CaptureAudio(phoneme), gen);
        break;

      case 'prompt4a':
        await playSegments(getPrompt4aAudio(phoneme), gen);
        if (!isStale()) {
          setStep('prompt4a_video');
          setActiveVideo('lowercase_tracing');
        }
        return;

      case 'prompt5a':
        await playSegments(getPrompt5aAudio(phoneme), gen);
        if (!isStale()) {
          setStep('prompt5a_video');
          setActiveVideo('uppercase_tracing');
        }
        return;

      case 'prompt4b':
        await playSegments(getPrompt4bAudio(phoneme), gen);
        break;

      case 'prompt5b':
        await playSegments(getPrompt5bAudio(phoneme), gen);
        break;

      case 'awaiting_scan': {
        const nextPhoneme = CARD_ORDER[currentCardIndex];
        if (nextPhoneme) {
          await playSegments(getScanPromptAudio(nextPhoneme), gen);
        }
        break;
      }

      default:
        break;
    }
  }, [playSegments, currentCardIndex]);

  // ── Advance to next step ────────────────────────────────────────────────

  const advanceTo = useCallback((nextStep: SessionStep, card: ScannedCard) => {
    setStep(nextStep);
    setActiveVideo(null);
    const gen = flowGenRef.current;
    executeStep(nextStep, card, gen);
  }, [executeStep]);

  // ── QR Scan Handler ─────────────────────────────────────────────────────

  const handleQRScan = useCallback(async (code: string) => {
    console.log('[ReviewFlow] QR scanned:', code);

    abortRef.current = true;
    flowGenRef.current += 1;
    stopAllAudio();
    setActiveVideo(null);

    // Record interrupted card as "still learning"
    if (scannedCard && step !== 'awaiting_scan' && step !== 'prompt6' && step !== 'session_complete') {
      phonemesScanned.current.push({
        phoneme: scannedCard.phoneme,
        displayName: scannedCard.displayName,
        skillId: scannedCard.skillId,
        timestamp: new Date(),
        wasSuccessful: false,
        masteryFeedback: 'learning',
      });
    }

    setIsPlaying(false);

    const { data: qrCode, error } = await lookupQRCode(code);

    if (error || !qrCode) {
      showFeedback(false, error?.message || t("This doesn't look like a Turn letter card. Please scan the QR code on a Turn consonant card."));
      abortRef.current = false;
      return;
    }

    const payload = qrCode.action_payload as {
      phoneme?: string;
      displayName?: string;
      illustrationWord?: string;
      illustrationDescription?: string;
      coloringVideo?: string;
      lowercaseImageLiveVideo?: string;
      uppercaseImageLiveVideo?: string;
    } | null;

    const phoneme = payload?.phoneme || '';

    if (!phoneme) {
      showFeedback(false, t('This QR code has no phoneme configured'));
      abortRef.current = false;
      return;
    }

    const foundSkillId = await getSkillIdForPhoneme(phoneme);

    const cardImages: CardImage[] = qrCode.card_images?.length > 0
      ? qrCode.card_images
      : qrCode.card_image_path
        ? [{ type: 'plain' as const, path: qrCode.card_image_path }]
        : [];

    const card: ScannedCard = {
      qrCode,
      cardImages,
      phoneme,
      displayName: payload?.displayName || phoneme,
      skillId: foundSkillId || undefined,
      legacyImagePath: qrCode.card_image_path || null,
      illustrationWord: payload?.illustrationWord || undefined,
      illustrationDescription: payload?.illustrationDescription || undefined,
      coloringVideo: payload?.coloringVideo || undefined,
      lowercaseImageLiveVideo: payload?.lowercaseImageLiveVideo || undefined,
      uppercaseImageLiveVideo: payload?.uppercaseImageLiveVideo || undefined,
    };

    setScannedCard(card);
    illustrationWordRef.current = card.illustrationWord;
    showFeedback(true, `${t('Scanned:')} ${card.displayName}`);
    clearPhraseCache();

    abortRef.current = false;
    setStep('prompt1');
    const gen = flowGenRef.current;
    executeStep('prompt1', card, gen);
  }, [showFeedback, executeStep, scannedCard, step, expectedPhoneme]);

  // ── Button Handlers ─────────────────────────────────────────────────────

  const handleRestart = useCallback(() => {
    if (!scannedCard) return;
    stopAllAudio();
    setActiveVideo(null);

    // For video sub-steps, restart the parent audio step
    let restartStep = step;
    if (step === 'prompt2_video') restartStep = 'prompt2_pre_video';
    if (step === 'prompt4a_video') restartStep = 'prompt4a';
    if (step === 'prompt5a_video') restartStep = 'prompt5a';

    setStep(restartStep);
    const gen = flowGenRef.current;
    executeStep(restartStep, scannedCard, gen);
  }, [scannedCard, step, executeStep]);

  const handleContinue = useCallback(() => {
    if (!scannedCard) return;
    stopAllAudio();
    setActiveVideo(null);

    const nextStepMap: Partial<Record<SessionStep, SessionStep>> = {
      prompt1: 'prompt2_pre_video',
      prompt2_pre_video: 'prompt2_post_video',
      prompt2_video: 'prompt2_post_video',
      prompt2_post_video: 'prompt3',
      prompt3: 'prompt3_capture',
      prompt3_capture: 'prompt4a',
      prompt4a: 'prompt4b',
      prompt4a_video: 'prompt4b',
      prompt4b: 'prompt5a',
      prompt5a: 'prompt5b',
      prompt5a_video: 'prompt5b',
      prompt5b: 'prompt6',
    };

    const next = nextStepMap[step];
    if (next) {
      advanceTo(next, scannedCard);
    }
  }, [scannedCard, step, advanceTo]);

  const handleShowMeHow = useCallback(() => {
    if (!scannedCard) return;
    stopAllAudio();

    // Show Me How replays the passive video without changing the logical step
    if (step === 'prompt4b') {
      setActiveVideo('lowercase_tracing');
    } else if (step === 'prompt5b') {
      setActiveVideo('uppercase_tracing');
    }
  }, [scannedCard, step]);

  const handleVideoEnded = useCallback(() => {
    if (!scannedCard) return;
    const endedVideo = activeVideo;
    setActiveVideo(null);

    if (step === 'prompt2_video') {
      if (endedVideo === 'lowercase_image_live' && scannedCard.uppercaseImageLiveVideo) {
        setActiveVideo('uppercase_image_live');
      } else {
        setStep('prompt2_post_video');
        const gen = flowGenRef.current;
        executeStep('prompt2_post_video', scannedCard, gen);
      }
    } else if (step === 'prompt4a_video') {
      setStep('prompt4b');
      const gen = flowGenRef.current;
      executeStep('prompt4b', scannedCard, gen);
    } else if (step === 'prompt5a_video') {
      setStep('prompt5b');
      const gen = flowGenRef.current;
      executeStep('prompt5b', scannedCard, gen);
    } else if (step === 'prompt4b' || step === 'prompt5b') {
      // Show Me How video ended — stay on current step, just clear video
    }
  }, [scannedCard, step, activeVideo, executeStep]);

  // ── Mastery Selection ───────────────────────────────────────────────────

  const handleMasterySelection = useCallback((feedback: 'learning' | 'mastered') => {
    if (!scannedCard) return;

    phonemesScanned.current.push({
      phoneme: scannedCard.phoneme,
      displayName: scannedCard.displayName,
      skillId: scannedCard.skillId,
      timestamp: new Date(),
      wasSuccessful: feedback === 'mastered',
      masteryFeedback: feedback,
    });

    const message = feedback === 'mastered'
      ? `${childName} ${t('mastered')} ${scannedCard.displayName}!`
      : `${childName} ${t('is still learning')} ${scannedCard.displayName}`;
    showFeedback(true, message, 2000);

    const cardPhoneme = scannedCard.phoneme.toLowerCase();
    const wasExpected = cardPhoneme === expectedPhoneme.toLowerCase();

    setCompletedCards(prev => {
      const updated = [...prev, cardPhoneme];
      return updated;
    });
    clearPhraseCache();
    setScannedCard(null);
    setActiveVideo(null);

    if (wasExpected) {
      // Advance past this card and any already-completed cards ahead
      let nextIndex = currentCardIndex + 1;
      while (nextIndex < CARD_ORDER.length && completedCards.includes(CARD_ORDER[nextIndex])) {
        nextIndex++;
      }
      setCurrentCardIndex(nextIndex);

      if (nextIndex >= CARD_ORDER.length) {
        setStep('session_complete');
      } else {
        setStep('awaiting_scan');
        const gen = flowGenRef.current;
        playSegments(getScanPromptAudio(CARD_ORDER[nextIndex]), gen);
      }
    } else {
      // Out-of-sequence card completed — nudge back to suggested order
      setStep('awaiting_scan');
      const gen = flowGenRef.current;
      playSegments(getNudgeAudio(expectedPhoneme), gen);
    }
  }, [scannedCard, childName, showFeedback, currentCardIndex, expectedPhoneme, completedCards, playSegments]);

  // ── Error Handler ───────────────────────────────────────────────────────

  const handleQRError = useCallback((error: string) => {
    console.error('[ReviewFlow] Scanner error:', error);
    showFeedback(false, error);
  }, [showFeedback]);

  // Clear feedback timer on unmount to avoid setState on unmounted component
  useEffect(() => {
    return () => {
      if (feedbackTimerRef.current) clearTimeout(feedbackTimerRef.current);
    };
  }, []);

  return {
    step,
    scannedCard,
    isPlaying,
    activeVideo,
    scanFeedback,
    currentCardIndex,
    expectedPhoneme,
    completedCards,
    stepLabels,
    handleQRScan,
    handleQRError,
    handleRestart,
    handleContinue,
    handleShowMeHow,
    handleMasterySelection,
    handleVideoEnded,
    phonemesScanned,
  };
}
